import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { getAssessmentReport, getAssessmentTrend, getCoreClasses } from "../../lib/api";
import { printPage } from "../../lib/print";

const CATS = ["participation_level", "engagement", "communication", "behavior", "attention", "work_completion"];
const CAT_SHORT = { participation_level: "Attend", engagement: "Engage", communication: "Comm", behavior: "Behav", attention: "Attn", work_completion: "Task" };
const CAT_FULL = { participation_level: "Attendance", engagement: "Engagement", communication: "Communication", behavior: "Behavior", attention: "Attention", work_completion: "Task Completion" };
const CAT_COLORS = { participation_level: "#00305E", engagement: "#EA580C", communication: "#16A34A", behavior: "#9333EA", attention: "#0891B2", work_completion: "#CA8A04" };
const INDICATORS = [{ value: "all", label: "All indicators" }, ...CATS.map((c) => ({ value: c, label: CAT_FULL[c] }))];
const PRIMARY_COLOR = "#00305E";
const COMPARE_COLOR = "#EA580C";

const scoredOf = (entries) => entries.filter((e) => !e.did_not_attend);
// Average of a single category across entries.
function catAvg(entries, cat) {
  const vals = scoredOf(entries).map((e) => e[cat]).filter((v) => v != null);
  return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
}
// Value for the selected indicator: single category avg, or mean across all 6 when "all".
function valueFor(entries, indicator) {
  if (indicator !== "all") return catAvg(entries, indicator);
  const vals = scoredOf(entries).flatMap((e) => CATS.map((c) => e[c]).filter((v) => v != null));
  return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
}
const allEntries = (report) => (report ? Object.values(report.students).flatMap((s) => s.entries) : []);
function entriesForClass(report, cls) {
  return report ? Object.values(report.students).flatMap((s) => s.entries.filter((e) => e.class_name === cls)) : [];
}
const round1 = (v) => (v != null ? Number(v.toFixed(1)) : null);

// Subject summary card (6 category tiles) — used in the By Subject section.
function ClassSummaryCard({ title, entries, testid, highlight }) {
  return (
    <div className={`border rounded-lg p-4 ${highlight ? "border-[#00305E] bg-[#00305E]/5" : ""}`} data-testid={testid}>
      <h4 className="font-semibold mb-2">{title}</h4>
      <p className="text-xs text-muted-foreground mb-2">{scoredOf(entries).length} scored entries</p>
      <div className="grid grid-cols-6 gap-1.5">
        {CATS.map((c) => {
          const v = catAvg(entries, c);
          const color = !v ? "bg-gray-100" : v >= 3 ? "bg-green-100 text-green-800" : v >= 2 ? "bg-yellow-100 text-yellow-800" : "bg-red-100 text-red-800";
          return (
            <div key={c} className={`p-2 rounded text-center ${color}`}>
              <div className="text-sm font-bold">{v ? v.toFixed(1) : "-"}</div>
              <div className="text-[10px]">{CAT_SHORT[c]}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AssessmentReportTab({ students, formerStudents, districts, startDate, endDate, setStartDate, setEndDate }) {
  const [assessmentReport, setAssessmentReport] = useState(null);
  const [compareReport, setCompareReport] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [assessmentReportLoading, setAssessmentReportLoading] = useState(false);
  const [assessmentStatusFilter, setAssessmentStatusFilter] = useState("current");
  const [assessmentDistrictFilter, setAssessmentDistrictFilter] = useState("all");
  const [assessmentStudentFilter, setAssessmentStudentFilter] = useState("all");
  const [coreClasses, setCoreClasses] = useState([]);
  const [indicator, setIndicator] = useState("all");
  const [compareMode, setCompareMode] = useState("category"); // category | subject
  // Comparison period
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [compareStart, setCompareStart] = useState("");
  const [compareEnd, setCompareEnd] = useState("");
  // Trend window
  const [trendWindow, setTrendWindow] = useState("school_year"); // school_year | fall | spring | custom
  const [trendStart, setTrendStart] = useState("");
  const [trendEnd, setTrendEnd] = useState("");

  useEffect(() => {
    getCoreClasses().then((r) => setCoreClasses(r.data || [])).catch(() => setCoreClasses([]));
  }, []);

  // Shared student-filter params for the report + trend calls.
  const baseParams = () => {
    const base = { student_status: assessmentStatusFilter };
    if (assessmentDistrictFilter !== "all") base.school_district = assessmentDistrictFilter;
    if (assessmentStudentFilter !== "all") base.student_id = assessmentStudentFilter;
    return base;
  };

  // Resolve the trend date window from the chosen preset (school year covers the primary start date).
  const trendWindowRange = () => {
    const d = startDate ? new Date(`${startDate}T00:00:00`) : new Date();
    const y = d.getFullYear();
    const syStart = d.getMonth() >= 7 ? y : y - 1; // school year starting Aug 1
    if (trendWindow === "fall") return { start: `${syStart}-08-01`, end: `${syStart}-12-31`, label: `Fall ${syStart}` };
    if (trendWindow === "spring") return { start: `${syStart + 1}-01-01`, end: `${syStart + 1}-06-30`, label: `Spring ${syStart + 1}` };
    if (trendWindow === "custom") return { start: trendStart, end: trendEnd, label: `${trendStart || "…"} → ${trendEnd || "…"}` };
    return { start: `${syStart}-08-01`, end: `${syStart + 1}-07-31`, label: `School Year ${syStart}–${syStart + 1}` };
  };

  const fetchTrend = async () => {
    const { start, end } = trendWindowRange();
    if (trendWindow === "custom" && (!start || !end)) { setTrendData([]); return; }
    try {
      const res = await getAssessmentTrend({ ...baseParams(), start_date: start, end_date: end });
      setTrendData(res.data.trend || []);
    } catch { /* trend is best-effort */ }
  };

  const handleGenerate = async () => {
    setAssessmentReportLoading(true);
    try {
      const base = baseParams();
      const primaryRes = await getAssessmentReport({ ...base, start_date: startDate, end_date: endDate });
      setAssessmentReport(primaryRes.data);

      if (compareEnabled && compareStart && compareEnd) {
        const cmpRes = await getAssessmentReport({ ...base, start_date: compareStart, end_date: compareEnd });
        setCompareReport(cmpRes.data);
      } else {
        setCompareReport(null);
      }
      await fetchTrend();
    } catch { toast.error("Failed to load assessment report"); }
    finally { setAssessmentReportLoading(false); }
  };

  // Re-pull just the trend when the window changes (once a report is loaded).
  useEffect(() => {
    if (assessmentReport) fetchTrend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trendWindow, trendStart, trendEnd]);

  const studentList = useMemo(() => {
    if (assessmentStatusFilter === "former") return formerStudents;
    if (assessmentStatusFilter === "all") return [...students, ...formerStudents];
    return students;
  }, [assessmentStatusFilter, students, formerStudents]);

  const filteredSortedStudents = useMemo(
    () =>
      [...studentList]
        .filter((s) => assessmentDistrictFilter === "all" || s.school_district === assessmentDistrictFilter)
        .sort((a, b) => a.last_name.localeCompare(b.last_name)),
    [studentList, assessmentDistrictFilter],
  );

  const cellColor = (v) => !v ? "" : v >= 3 ? "text-green-700 font-semibold" : v >= 2 ? "text-yellow-700" : "text-red-700 font-semibold";

  const sortedReportStudents = useMemo(() => {
    if (!assessmentReport?.students) return [];
    return Object.entries(assessmentReport.students).sort(([, a], [, b]) => a.student_name.localeCompare(b.student_name));
  }, [assessmentReport]);

  // Split this report's classes into core (config order) + electives (the rest).
  const groupedClasses = useMemo(() => {
    if (!assessmentReport?.classes) return { core: [], electives: [] };
    const present = assessmentReport.classes;
    const core = coreClasses.filter((c) => present.includes(c));
    const coreSet = new Set(core);
    const electives = present.filter((c) => !coreSet.has(c)).sort((a, b) => a.localeCompare(b));
    return { core, electives };
  }, [assessmentReport, coreClasses]);

  // Core-first ordered union of subjects across both periods (for By-Subject compare).
  const comparisonSubjects = useMemo(() => {
    const set = new Set([...(assessmentReport?.classes || []), ...(compareReport?.classes || [])]);
    const core = coreClasses.filter((c) => set.has(c));
    const coreSet = new Set(core);
    const electives = [...set].filter((c) => c && !coreSet.has(c)).sort((a, b) => a.localeCompare(b));
    return [...core, ...electives];
  }, [assessmentReport, compareReport, coreClasses]);

  // Comparison chart data (respects indicator + mode).
  const comparisonChartData = useMemo(() => {
    if (!assessmentReport) return [];
    const withCompare = (row, pVal, cVal) => ({
      name: row,
      Primary: round1(pVal) ?? 0,
      ...(compareReport ? { Comparison: round1(cVal) ?? 0 } : {}),
    });
    if (compareMode === "subject") {
      return comparisonSubjects.map((subj) =>
        withCompare(subj, valueFor(entriesForClass(assessmentReport, subj), indicator), valueFor(entriesForClass(compareReport, subj), indicator)),
      );
    }
    const cats = indicator === "all" ? CATS : [indicator];
    return cats.map((c) =>
      withCompare(CAT_SHORT[c] || CAT_FULL[c], catAvg(allEntries(assessmentReport), c), catAvg(allEntries(compareReport), c)),
    );
  }, [assessmentReport, compareReport, compareMode, indicator, comparisonSubjects]);

  const rangeLabel = (a, b) => `${a || "…"} → ${b || "…"}`;

  const trendLines = indicator === "all" ? CATS : [indicator];

  const handleExportPdf = () => {
    if (!assessmentReport) return;
    const cmpSvg = document.querySelector('[data-testid="assessment-comparison-chart"] svg');
    const trendSvg = document.querySelector('[data-testid="assessment-trend-chart"] svg');
    const indicatorLabel = indicator === "all" ? "All indicators" : CAT_FULL[indicator];

    let content = `<div class="section"><div class="field-label">Primary period</div><div class="field-value">${rangeLabel(startDate, endDate)}</div></div>`;
    if (compareReport) content += `<div class="section"><div class="field-label">Comparison period</div><div class="field-value">${rangeLabel(compareStart, compareEnd)}</div></div>`;
    content += `<div class="section"><div class="field-label">Indicator focus</div><div class="field-value">${indicatorLabel}</div></div>`;

    if (compareReport && cmpSvg) {
      content += `<h2>Time Frame Comparison (${compareMode === "subject" ? "by subject" : "by category"})</h2><div>${cmpSvg.outerHTML}</div>`;
      // comparison table
      let rows = comparisonChartData.map((d) => {
        const delta = d.Comparison != null ? (d.Primary - d.Comparison) : null;
        return `<tr><td>${d.name}</td><td>${d.Primary ?? "-"}</td><td>${d.Comparison ?? "-"}</td><td>${delta == null ? "-" : (delta > 0 ? "+" : "") + delta.toFixed(1)}</td></tr>`;
      }).join("");
      content += `<table><thead><tr><th>${compareMode === "subject" ? "Subject" : "Category"}</th><th>Primary</th><th>Comparison</th><th>Change</th></tr></thead><tbody>${rows}</tbody></table>`;
    }
    if (trendSvg && trendData.length) {
      content += `<h2>School-Year Trend (${indicatorLabel})</h2><div>${trendSvg.outerHTML}</div>`;
    }
    printPage({ title: "Assessment Comparison Report", subtitle: "SMARTIS — Reports", content, landscape: true, extraStyles: `svg { max-width: 100%; height: auto; }` });
  };

  return (
    <Card>
      <CardHeader>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div><CardTitle>Assessment Reports</CardTitle><CardDescription>View assessment scores by student and class</CardDescription></div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" data-testid="assessment-export-pdf-btn" disabled={!assessmentReport || assessmentReport.total_assessments === 0} onClick={handleExportPdf}>Export PDF</Button>
              <Button size="sm" className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="load-assessment-report-btn" disabled={assessmentReportLoading} onClick={handleGenerate}>{assessmentReportLoading ? "Loading..." : "Generate Report"}</Button>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-auto" data-testid="assessment-primary-start" />
            <span className="text-muted-foreground text-sm">to</span>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-auto" data-testid="assessment-primary-end" />
            <Select value={assessmentStatusFilter} onValueChange={(v) => { setAssessmentStatusFilter(v); setAssessmentStudentFilter("all"); }}>
              <SelectTrigger className="w-[160px]" data-testid="assessment-report-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="current">Current Students</SelectItem><SelectItem value="former">Former Students</SelectItem><SelectItem value="all">All Students</SelectItem></SelectContent>
            </Select>
            <Select value={assessmentDistrictFilter} onValueChange={setAssessmentDistrictFilter}>
              <SelectTrigger className="w-[180px]" data-testid="assessment-report-district-filter"><SelectValue placeholder="All Districts" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Districts</SelectItem>{districts.map(d => <SelectItem key={d.id || d.name} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={assessmentStudentFilter} onValueChange={setAssessmentStudentFilter}>
              <SelectTrigger className="w-[200px]" data-testid="assessment-report-student-filter"><SelectValue placeholder="All Students" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Students</SelectItem>{filteredSortedStudents.map(s => (<SelectItem key={s.id} value={s.id}>{s.last_name}, {s.preferred_name || s.first_name}{s.status === "former" ? " (Former)" : ""}</SelectItem>))}</SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground">Indicator:</Label>
              <Select value={indicator} onValueChange={setIndicator}>
                <SelectTrigger className="w-[170px]" data-testid="assessment-indicator-filter"><SelectValue /></SelectTrigger>
                <SelectContent>{INDICATORS.map((i) => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {/* Comparison period controls */}
          <div className="flex flex-wrap items-center gap-3 pt-1 border-t border-dashed">
            <label className="flex items-center gap-2 cursor-pointer pt-2">
              <Checkbox checked={compareEnabled} onCheckedChange={(c) => setCompareEnabled(!!c)} data-testid="assessment-compare-toggle" />
              <span className="text-sm font-medium">Compare to another time period</span>
            </label>
            {compareEnabled && (
              <div className="flex flex-wrap items-center gap-2 pt-2">
                <Label className="text-xs text-muted-foreground">Comparison:</Label>
                <Input type="date" value={compareStart} onChange={(e) => setCompareStart(e.target.value)} className="w-auto" data-testid="assessment-compare-start" />
                <span className="text-muted-foreground text-sm">to</span>
                <Input type="date" value={compareEnd} onChange={(e) => setCompareEnd(e.target.value)} className="w-auto" data-testid="assessment-compare-end" />
              </div>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!assessmentReport ? (
          <p className="text-center py-12 text-muted-foreground">Select a date range and click Generate Report</p>
        ) : assessmentReport.total_assessments === 0 ? (
          <p className="text-center py-12 text-muted-foreground">No assessment data for this period</p>
        ) : (
          <div className="space-y-6">
            <div className="text-sm text-muted-foreground">{assessmentReport.total_assessments} assessments across {Object.keys(assessmentReport.students).length} students | Classes: {assessmentReport.classes.join(", ")}</div>

            {/* Time-frame comparison bar chart */}
            {compareReport && (
              <div className="border rounded-lg p-4" data-testid="assessment-comparison-chart">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                  <h3 className="font-semibold text-lg">Time Frame Comparison</h3>
                  <div className="inline-flex rounded-md border overflow-hidden text-sm">
                    <button type="button" onClick={() => setCompareMode("category")} className={`px-3 py-1 ${compareMode === "category" ? "bg-[#00305E] text-white" : "bg-white text-[#00305E]"}`} data-testid="assessment-compare-mode-category">By Category</button>
                    <button type="button" onClick={() => setCompareMode("subject")} className={`px-3 py-1 ${compareMode === "subject" ? "bg-[#00305E] text-white" : "bg-white text-[#00305E]"}`} data-testid="assessment-compare-mode-subject">By Subject</button>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  {indicator === "all" ? "Average scores (1–4)" : `${CAT_FULL[indicator]} average (1–4)`} per period, {compareMode === "subject" ? "by subject" : "by category"}.
                  <span className="inline-flex items-center gap-1 ml-2"><span className="inline-block w-3 h-3 rounded-sm" style={{ background: PRIMARY_COLOR }} /> Primary {rangeLabel(startDate, endDate)}</span>
                  <span className="inline-flex items-center gap-1 ml-3"><span className="inline-block w-3 h-3 rounded-sm" style={{ background: COMPARE_COLOR }} /> Comparison {rangeLabel(compareStart, compareEnd)}</span>
                </p>
                {compareReport.total_assessments === 0 && <p className="text-xs text-amber-600 mb-2">No assessment data in the comparison period.</p>}
                <div style={{ width: "100%", height: 320 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={comparisonChartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" interval={0} angle={compareMode === "subject" ? -20 : 0} textAnchor={compareMode === "subject" ? "end" : "middle"} height={compareMode === "subject" ? 60 : 30} />
                      <YAxis domain={[0, 4]} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="Primary" name={`Primary (${rangeLabel(startDate, endDate)})`} fill={PRIMARY_COLOR} radius={[3, 3, 0, 0]} />
                      <Bar dataKey="Comparison" name={`Comparison (${rangeLabel(compareStart, compareEnd)})`} fill={COMPARE_COLOR} radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* School-year trend */}
            <div className="border rounded-lg p-4" data-testid="assessment-trend-chart">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                <h3 className="font-semibold text-lg">Trend</h3>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground">Window:</Label>
                  <Select value={trendWindow} onValueChange={setTrendWindow}>
                    <SelectTrigger className="w-[170px]" data-testid="assessment-trend-window"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="school_year">School Year (auto)</SelectItem>
                      <SelectItem value="fall">Fall Term</SelectItem>
                      <SelectItem value="spring">Spring Term</SelectItem>
                      <SelectItem value="custom">Custom Range</SelectItem>
                    </SelectContent>
                  </Select>
                  {trendWindow === "custom" && (
                    <>
                      <Input type="date" value={trendStart} onChange={(e) => setTrendStart(e.target.value)} className="w-auto" data-testid="assessment-trend-start" />
                      <span className="text-muted-foreground text-sm">to</span>
                      <Input type="date" value={trendEnd} onChange={(e) => setTrendEnd(e.target.value)} className="w-auto" data-testid="assessment-trend-end" />
                    </>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                Monthly {indicator === "all" ? "category" : CAT_FULL[indicator]} averages (1–4) · {trendWindowRange().label}
              </p>
              {trendData.length === 0 ? (
                <p className="text-sm text-amber-600 py-6 text-center" data-testid="assessment-trend-empty">No assessment data in the selected trend window.</p>
              ) : (
                <div style={{ width: "100%", height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trendData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" />
                      <YAxis domain={[0, 4]} />
                      <Tooltip />
                      <Legend />
                      {trendLines.map((c) => (
                        <Line key={c} type="monotone" dataKey={c} name={CAT_FULL[c]} stroke={CAT_COLORS[c]} strokeWidth={2} dot={{ r: 2 }} connectNulls />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead><tr className="bg-[#0d2240] text-white"><th className="px-3 py-2 text-left font-semibold sticky left-0 bg-[#0d2240] z-10">Student</th><th className="px-2 py-2 text-left font-semibold text-xs">District</th><th className="px-2 py-2 text-center font-semibold text-xs">Status</th><th className="px-2 py-2 text-center font-semibold text-xs">Entries</th><th className="px-2 py-2 text-center font-semibold text-xs bg-orange-500/90">DNA</th><th className="px-2 py-2 text-center font-semibold text-xs">Attend Avg</th><th className="px-2 py-2 text-center font-semibold text-xs">Engage Avg</th><th className="px-2 py-2 text-center font-semibold text-xs">Comm Avg</th><th className="px-2 py-2 text-center font-semibold text-xs">Behav Avg</th><th className="px-2 py-2 text-center font-semibold text-xs">Attn Avg</th><th className="px-2 py-2 text-center font-semibold text-xs">Task Avg</th><th className="px-2 py-2 text-center font-semibold text-xs">Overall</th></tr></thead>
                <tbody>
                  {sortedReportStudents.map(([sid, data]) => {
                    const avgs = data.averages || {};
                    const vals = CATS.map(c => avgs[c]).filter(v => v != null);
                    const overall = vals.length > 0 ? (vals.reduce((a,b)=>a+b,0) / vals.length) : null;
                    return (
                      <tr key={sid} className="border-b hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium sticky left-0 bg-white z-10">{data.student_name}</td>
                        <td className="px-2 py-2 text-xs">{data.school_district || "-"}</td>
                        <td className="px-2 py-2 text-center text-xs">{data.status === "former" ? <span className="bg-amber-100 text-amber-800 text-[10px] px-1.5 py-0.5 rounded">Former</span> : <span className="bg-green-100 text-green-800 text-[10px] px-1.5 py-0.5 rounded">Current</span>}</td>
                        <td className="px-2 py-2 text-center">{data.total_entries}</td>
                        <td className="px-2 py-2 text-center">{data.dna_count > 0 ? <span className="inline-block bg-orange-100 text-orange-700 font-bold px-2.5 py-0.5 rounded-full text-sm" data-testid={`report-dna-${sid}`}>{data.dna_count}</span> : <span className="text-gray-300">0</span>}</td>
                        {CATS.map(c => (<td key={c} className={`px-2 py-2 text-center ${cellColor(avgs[c])} ${indicator !== "all" && indicator === c ? "bg-blue-50" : ""}`}>{avgs[c] ? avgs[c].toFixed(1) : "-"}</td>))}
                        <td className={`px-2 py-2 text-center font-bold ${cellColor(overall)}`}>{overall ? overall.toFixed(1) : "-"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* By subject: core classes first, then a combined Electives section, then each elective */}
            <div data-testid="assessment-by-class">
              <h3 className="font-semibold text-lg mb-3">By Subject</h3>
              {groupedClasses.core.length > 0 && (
                <div className="mb-5" data-testid="assessment-core-section">
                  <h4 className="text-sm font-semibold text-[#00305E] uppercase tracking-wide mb-2">Core Classes</h4>
                  <div className="grid gap-4 md:grid-cols-2">
                    {groupedClasses.core.map((cls) => {
                      const entries = scoredOf(entriesForClass(assessmentReport, cls));
                      if (entries.length === 0) return null;
                      return <ClassSummaryCard key={cls} title={cls} entries={entries} testid={`assessment-class-${cls}`} />;
                    })}
                  </div>
                </div>
              )}
              {groupedClasses.electives.length > 0 && (
                <div data-testid="assessment-electives-section">
                  <h4 className="text-sm font-semibold text-[#00305E] uppercase tracking-wide mb-2">Electives</h4>
                  <div className="grid gap-4 md:grid-cols-2">
                    <ClassSummaryCard
                      title={`All Electives (${groupedClasses.electives.length} classes)`}
                      entries={scoredOf(groupedClasses.electives.flatMap((c) => entriesForClass(assessmentReport, c)))}
                      testid="assessment-electives-combined"
                      highlight
                    />
                    {groupedClasses.electives.map((cls) => {
                      const entries = scoredOf(entriesForClass(assessmentReport, cls));
                      if (entries.length === 0) return null;
                      return <ClassSummaryCard key={cls} title={cls} entries={entries} testid={`assessment-class-${cls}`} />;
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
